import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Card } from '@shared/ui/card';
import { DynamicForm } from '@shared/components/DynamicForm';
import { FormField } from '@shared/services/FormServices';
import { FIELD_CLASSES } from '@shared/utils/constants';
import { Button } from '@shared/ui/button';
import { useTranslation } from "react-i18next";

interface EditPersonalInfoProps {
  consumerData: any;
  onCancel: () => void;
  onSave: (data: any) => void;
}

export const EditPersonalInfo: React.FC<EditPersonalInfoProps> = ({ consumerData, onCancel, onSave }) => {
  const { t } = useTranslation();
  const formatAddress = (addressObj: any) => {
    if (!addressObj) return "";
    return addressObj.address || addressObj.ADDRESS || "";
  };

  const form = useForm({
    defaultValues: {
      firstName: consumerData?.firstName || '',
      lastName: consumerData?.lastName || '',
      email: consumerData?.email || '',
      phoneNumber: consumerData?.contactNumber || '',
      serviceAddress: formatAddress(consumerData?.addressData?.service),
      mailingAddress: formatAddress(consumerData?.addressData?.billing),
      ssnTaxId: consumerData?.ssn || '',
      dateOfBirth: consumerData?.dateOfBirth || '',
      coApplicantName: `${consumerData?.additionalData?.secondaryConsumer?.firstName || ''} ${consumerData?.additionalData?.secondaryConsumer?.lastName || ''}`.trim(),
      coApplicantEmail: consumerData?.additionalData?.secondaryConsumer?.email || '',
      coApplicantPhone: consumerData?.additionalData?.secondaryConsumer?.contactNumber || '',
      coApplicantRelation: consumerData?.additionalData?.secondaryConsumer?.relation || '',
    }
  });

  const fields: FormField[] = useMemo(() => [
    {
      name: 'firstName',
      label: t("First Name"),
      type: 'text',
      className: 'col-span-6',
      group: t("Personal Information"),
      groupColorNeeded:false,
      groupBorderNeeded:true,
    },
    {
      name: 'lastName',
      label: t("Last Name"),
      type: 'text',
      className: 'col-span-6',
      group: t("Personal Information"),
            groupColorNeeded:false,
      groupBorderNeeded:true,
    },
    {
      name: 'email',
      label: t("Email Address"),
      type: 'email',
      className: 'col-span-6',
      group: t("Personal Information"),
            groupColorNeeded:false,
      groupBorderNeeded:true,
    },
    {
      name: 'phoneNumber',
      label: t("Phone Number"),
      type: 'text',
      className: 'col-span-6',
      group: t("Personal Information"),
            groupColorNeeded:false,
      groupBorderNeeded:true,
    },
    {
      name: 'serviceAddress',
      label: t("Service Address"),
      type: 'text',
      className: 'col-span-12',
      group: t("Personal Information"),
            groupColorNeeded:false,
      groupBorderNeeded:true,
    },
    {
      name: 'mailingAddress',
      label: t("Mailing Address"),
      type: 'text',
      className: 'col-span-12',
      helperText: t("Leave same as service address if identical"),
      group: t("Personal Information"),
            groupColorNeeded:false,
      groupBorderNeeded:true,
    },
    {
      name: 'ssnTaxId',
      label: t("SSN / Tax ID"),
      type: 'text',
      className: 'col-span-6',
      group: t("Additional Details"),
            groupColorNeeded:false,
      groupBorderNeeded:true,
    },
    {
      name: 'dateOfBirth',
      label: t("Date of Birth"),
      type: 'datepicker',
      className: 'col-span-6',
      group: t("Additional Details"),
      groupColorNeeded:false,
      groupBorderNeeded:true,
    },
    {
      name: 'coApplicantName',
      label: t("Name"),
      type: 'text',
      className: 'col-span-6',
      group: t("Co-Applicant Details"),
      groupColorNeeded:false,
      groupBorderNeeded:true,
    },
    {
      name: 'coApplicantEmail',
      label: t("Email"),
      type: 'email',
      className: 'col-span-6',
      group: t("Co-Applicant Details"),
      groupColorNeeded:false,
      groupBorderNeeded:true, 
    },
    {
      name: 'coApplicantPhone',
      label: t("Phone"),
      type: 'text',
      className: 'col-span-6',
      group: t("Co-Applicant Details"),
      groupColorNeeded:false,
      groupBorderNeeded:true,    
    },
    {
      name: 'coApplicantRelation',
      label: t("Relation"),
      type: 'text',
      className: 'col-span-6',
      group: t("Co-Applicant Details"),
      groupColorNeeded:false,
      groupBorderNeeded:true,  
    },
  ], [t]);

  const onSubmit = (data: any) => {
    console.log("Form Submitted:", data);
    onSave(data);
  };

  return (
    <Card className="flex-1 border border-slate-100 shadow-sm rounded-xl overflow-hidden h-fit">
      <div className="">
        <DynamicForm 
          form={form} 
          fields={fields} 
          gridColumns={12} 
          className="mb-8"
          renderCustomFooter={() => <></>}
          config={{ classes: FIELD_CLASSES }}
        />

        <div className="flex justify-end items-center gap-4 mt-8">
          <Button variant="outline" onClick={onCancel} className="px-6">
            {t("Cancel")}
          </Button>
          <Button onClick={form.handleSubmit(onSubmit)} className="px-6 bg-blue-600 hover:bg-blue-700 text-white">
            {t("Save Changes")}
          </Button>
        </div>
      </div>
    </Card>
  );
};
